import { useMemo } from 'react';
import styled from 'styled-components';
import {
  FlexCenter,
  FlexColumn,
  Snackbar,
  Navbar,
  Flex,
} from '@elementary/common-ui/components';
import { HEADER_HEIGHT } from '@elementary/common-ui/constants';
import * as atoms from '@elementary/common-ui/atoms';
import { CircularProgress } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { useRecoilState } from 'recoil';
import { Outlet } from 'react-router-dom';
import TestOverviewSidePanel from 'shared/components/TestOverviewSidePanel';
import useAccountSettings from 'shared/hooks/useAccountSettings';
import useEnvironments from 'shared/hooks/useEnvironments';
import useEnvironmentsPolling from 'shared/hooks/useEnvironmentsPolling';
import usePendingChangesPolling from 'shared/hooks/usePendingChangesPolling';
import useFlags from 'shared/hooks/useFlags';
import useSnackbar from 'shared/hooks/useSnackbar';
import useSupportChat from 'shared/hooks/useSupportChat';
import useTestsPolling from 'shared/hooks/useTestsPolling';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import { AiChatPanel } from '@elementary/common-ui/components';
import useNavbarItems from '../hooks/useNavbarItems';
import usePrefetchQueries from '../hooks/usePrefetchQueries';
import useResponsiveLayout from '../hooks/useResponsiveLayout';
import AppPageAlert from './AppPageAlert';
import ErrorPage from './ErrorPage';
import Header from './Header';
import PosthogProvider from './PosthogProvider';
import '@elementary/common-ui/utils/datetime';
import IntegrationIssueToasts from './IntegrationIssueToasts';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100vw;
  overflow: hidden;
`;

const LayoutRow = styled(Flex)`
  width: 100%;
  height: calc(100% - ${HEADER_HEIGHT}px);
  position: relative;
`;

const ResponsiveSection = styled(Flex)`
  flex: 1;
  min-width: 0;
  height: 100%;
`;

const Content = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const CenteringWrapper = styled(FlexCenter)`
  height: 100%;
  width: 100%;
`;

function AppShell() {
  const [navbarState, setNavbarState] = useRecoilState(atoms.navbarCollapsed);
  const isNavbarExpanded = !navbarState.isCollapsed;

  const {
    isLoading: isEnvLoading,
    isLoaded: isEnvLoaded,
    isError: isEnvError,
    hasNoEnvironmentAccess,
  } = useEnvironments();
  const { isLoading: isAccountSettingsLoading } = useAccountSettings();
  const { snackbar, closeSnackbar, openSnackbar } = useSnackbar();
  const supportChat = useSupportChat();
  const { isLoading: isFlagsLoading, isError: isFlagsError } = useFlags();
  const { data: account, isLoading: isAccountLoading } = useAccount();

  useEnvironmentsPolling();
  usePendingChangesPolling();
  useTestsPolling(true);

  usePrefetchQueries();

  const navbarItems = useNavbarItems();
  const { contentRef } = useResponsiveLayout();

  const onNavbarButtonClick = () => {
    setNavbarState((prev) => ({
      isCollapsed: !prev.isCollapsed,
      userOverride: prev.isCollapsed ? false : true,
    }));
  };

  const hasAiPanelAccess = useMemo(
    () => !!account?.ai_features_enabled,
    [account],
  );

  const isLoading =
    isEnvLoading ||
    isAccountLoading ||
    isFlagsLoading ||
    isAccountSettingsLoading ||
    isFlagsError ||
    (!isEnvLoaded && isEnvError);

  return (
    <Wrapper>
      {isLoading ? (
        <CenteringWrapper>
          <CircularProgress />
        </CenteringWrapper>
      ) : (
        <PosthogProvider>
          <Header
            onNavbarButtonClick={onNavbarButtonClick}
            isNavbarExpanded={isNavbarExpanded}
            hideNavbarButton={hasNoEnvironmentAccess}
            hasAiPanelAccess={hasAiPanelAccess}
          />
          <LayoutRow>
            <ResponsiveSection ref={contentRef}>
              {!hasNoEnvironmentAccess && (
                <Navbar
                  isExpanded={isNavbarExpanded}
                  items={navbarItems}
                  onSupportClick={supportChat.show}
                  onExpand={onNavbarButtonClick}
                />
              )}
              <Content>
                <AppPageAlert />
                <ErrorBoundary
                  fallbackRender={({ error }) => (
                    <ErrorPage error={error} page={'AppShell'} />
                  )}
                >
                  <Outlet />
                </ErrorBoundary>
              </Content>
            </ResponsiveSection>

            {hasAiPanelAccess && (
              <AiChatPanel
                onBlockedAction={(message) =>
                  openSnackbar({ variant: 'error', message })
                }
              />
            )}
          </LayoutRow>

          <Snackbar {...snackbar} onClose={closeSnackbar} />
          <IntegrationIssueToasts />

          <TestOverviewSidePanel />
        </PosthogProvider>
      )}
    </Wrapper>
  );
}

export default AppShell;
